import React, { useContext, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getDate } from '../../../../../utils/DateUtil'
import { ALIGN, NewTable, SORT } from 'components/datatable/NewTable'
import useListIndexed from 'utils/customHook/useListIndexed'
import PopinQualityCampaignPlanning from './PopinQualityCampaignPlanning'
import useBoolean from 'utils/customHook/useBoolean'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import { toString, compact, intersection, isNil } from 'lodash'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import PropTypes from 'prop-types'
import { negIdGenerator } from 'campaign/utils/CampaignUtils'

const headers = ['startDate', 'endDate', 'completion', 'nbPoints', 'parameterGroup', 'nbParam', 'nbExceptions']
const DEFAULT_PLANNING = { stationsPlan: [], parametersPlan: [] }

const TableQualityCampaignPlannings = ({ exportByPlannifActions }) => {
    const {
        isEditMode,
        campaignPlannings, setCampaignPlannings,
        campaignPlanningsExceptions, setCampaignPlanningsExceptions,
        campaignParameters,
    } = useContext(QualityCampaignContext)
    const {
        selections,
        contributors,
    } = useSelector(store => ({
        selections: store.ParameterReducer.selectionsWithParam,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)
    const indexedSelections = useListIndexed(selections, 'code')

    const { value: isOpen, setTrue: onOpen, setFalse: onClose } = useBoolean(false)
    const [selectedPlanningIndex, setSelectedPlanningIndex] = useState()

    const dispatch = useDispatch()

    const planningsFormated = campaignPlannings.map(p => {
        const mnemonique = contributors.find(({ id }) => id === p.producer)?.mnemonique
        return {
            startDate: { value: getDate(p.startDate), sortValue: p.startDate },
            endDate: { value: getDate(p.endDate), sortValue: p.endDate },
            completion: mnemonique ?? contributors.find(({ id }) => id === p.producer)?.name,
            nbStations: `${p.stationsPlan.length}`,
            nbParam: `${p.parametersPlan.length}`,
            parameterGroup: toString([
                ...p.selections.map(selection => indexedSelections[selection]?.name),
                ...compact([
                    intersection(
                        campaignParameters.filter(({ selectionCode }) => !selectionCode).map(({ id }) => id),
                        p.parametersPlan,
                    ).length > 0 ? i18n.outOfSelections : undefined,
                ]),
            ]).replace(/,/g, ', '),
            nbExceptions: campaignPlanningsExceptions.filter(({ planningId }) => planningId === p.id).length,
        }
    })

    const onUpdate = (index, newPlanning, newPlanningExceptions) => {
        setCampaignPlannings(prevCP => prevCP.toSpliced(index, 1, newPlanning))
        setCampaignPlanningsExceptions(prevCPE => [...prevCPE.filter(({ planningId }) => planningId !== campaignPlannings[index].id), ...newPlanningExceptions])
    }
    const onDuplicate = (index) => {
        const newPlanningId = negIdGenerator.next().value
        setCampaignPlannings(prevCP => [...prevCP, { ...prevCP[index], id: newPlanningId }])
        setCampaignPlanningsExceptions(prevCPE =>
            [
                ...prevCPE,
                ...prevCPE
                    .filter(({ planningId }) => planningId === campaignPlannings[index].id)
                    .map(pE => ({ ...pE, id: undefined, planningId: newPlanningId }))])
    }
    const onDelete = (index) => {
        setCampaignPlannings(prevCP => prevCP.toSpliced(index, 1))
        setCampaignPlanningsExceptions(prevCPE => prevCPE.filter(({ planningId }) => planningId !== campaignPlannings[index].id))
    }

    return (
        <>
            <NewTable
                rows={planningsFormated}
                headers={headers}
                lineActions={[{
                    icon: 'visibility',
                    onClick: (_, index) => {
                        setSelectedPlanningIndex(index)
                        onOpen()
                    },
                    displayed: !isEditMode,
                }, {
                    icon: 'close',
                    onClick: (_, index) => onDelete(index),
                    displayed: isEditMode,
                }, {
                    icon: 'add_to_photos', // duplicate
                    onClick: (_, index) => onDuplicate(index),
                    displayed: isEditMode,
                }, {
                    icon: 'edit',
                    onClick: (_, index) => {
                        setSelectedPlanningIndex(index)
                        onOpen()
                    },
                    displayed: isEditMode,
                },
                {
                    icon: 'file_download',
                    onClick: (planning, index) => {
                        if (isNil(parseInt(planning.nbParam)) && isNil(parseInt(planning.nbStations))) {
                            dispatch(ToastrAction.warning(i18n.cannotExportPlanning))
                        } else {
                            exportByPlannifActions(campaignPlannings[index]?.id)
                        }
                    },
                    tooltip: i18n.export,
                    align: ALIGN.RIGHT,
                },
                ]}
                defaultSort={{ column: 'startDate', direction: SORT.ASC }}
            />
            <PopinQualityCampaignPlanning
                isOpen={isOpen}
                onClose={onClose}
                onValidate={(newPlanning, newPlanningExceptions) => {
                    onUpdate(selectedPlanningIndex, newPlanning, newPlanningExceptions)
                    onClose()
                }}
                campaignPlanning={campaignPlannings[selectedPlanningIndex] ?? { ...DEFAULT_PLANNING, id: negIdGenerator.next().value }}
                campaignPlanningExceptions={campaignPlanningsExceptions.filter(({ planningId }) => planningId === campaignPlannings[selectedPlanningIndex]?.id)}
                isEditMode={isEditMode}
            />
        </>
    )
}

TableQualityCampaignPlannings.propTypes = {
    exportByPlannifActions: PropTypes.func,
}

export default TableQualityCampaignPlannings